import React, { useCallback, useEffect, useState } from 'react';
import { Layers, Plus, Pencil, Loader2, Users, BellRing } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../../ui-v2';
import { Badge, EmptyState, Modal, Input } from '../../ui-v2.5';
import { getLotes, renombrarLote } from '../api';
import ModalNuevoLote from './ModalNuevoLote';
import ModalPersonasLote from './ModalPersonasLote';
import ModalReavisarLote from './ModalReavisarLote';

/**
 * PanelLotes — los lotes (tandas) de una campaña, en DS v2.
 *
 * Una campaña nace con su «Lote 1» (el asistente). Con la campaña Activa o Pausada se añaden lotes
 * con otro criterio; quien ya está en la campaña, en cualquier lote, queda fuera y se dice por qué.
 * Añadir a una campaña Activa avisa sólo a los nuevos; en Pausada, al reanudar.
 *
 * Los nombres de lote los escribe un usuario: se pintan como texto, nunca como HTML.
 */

const ETIQUETA_CRITERIO = {
  negocio: 'Negocio',
  departamento: 'Departamento',
  empresa: 'Empresa',
  empresa_colaboradora: 'Empresa colaboradora',
};

export function textoCriterio(c) {
  if (!c) return 'Asistente de la campaña';
  if (c.tipo === 'manual') return `Selección manual (${c.personas})`;
  const valores = (c.valores || []).map((v) => v.nombre).join(', ');
  return `${ETIQUETA_CRITERIO[c.tipo] || c.tipo}: ${valores}`;
}

function fechaCorta(s) {
  if (!s) return null;
  return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

function fechaHoraCorta(s) {
  if (!s) return null;
  return new Date(s).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** «Volver a avisar»: con la campaña Activa, en un lote ya avisado y con alguien sin cita. */
export function puedeReavisar(campana, lote) {
  return campana.estado === 'Activa' && !!lote.notificado_en && lote.sin_cita > 0;
}

/**
 * Rediseño (Fase 1): tarjetas en vez de tabla, con las mismas cifras, el mismo aviso y las mismas
 * acciones. `onLotes(lotes)` avisa al padre de lo cargado (el resumen lo usa para «lotes sin avisar»).
 */
export default function PanelLotes({ campana, puedeGestionar, version, onCambio, onLotes }) {
  const [lotes, setLotes] = useState(null);
  const [error, setError] = useState(null);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [renombrando, setRenombrando] = useState(null); // { id, numero, nombre }
  const [editandoPersonas, setEditandoPersonas] = useState(null); // lote
  const [reavisando, setReavisando] = useState(null); // lote

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const l = await getLotes(campana.id);
      setLotes(l);
      onLotes?.(l);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudieron cargar los lotes.');
    }
  }, [campana.id, onLotes]);

  useEffect(() => { cargar(); }, [cargar, version]);

  const enCurso = ['Activa', 'Pausada'].includes(campana.estado);

  return (
    <section className="mb-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <h2 className="text-base font-medium text-v25-ink-900 flex items-center gap-2">
            <Layers className="w-4 h-4" /> Lotes
          </h2>
          <p className="text-xs text-v25-ink-500">
            Una persona está una sola vez en la campaña, en cualquiera de sus lotes.
          </p>
        </div>
        {puedeGestionar && (
          enCurso ? (
            <Button size="sm" onClick={() => setNuevoAbierto(true)}>
              <Plus className="w-3.5 h-3.5" /> Añadir lote
            </Button>
          ) : (
            campana.estado === 'Borrador' && (
              <span className="text-xs text-v25-ink-500">Los lotes se añaden con la campaña en curso.</span>
            )
          )
        )}
      </div>

      {error ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3">{error}</div>
      ) : lotes === null ? (
        <div className="text-sm text-v25-ink-500 py-4"><Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />Cargando lotes…</div>
      ) : lotes.length === 0 ? (
        <EmptyState variant="minimal" title="Esta campaña aún no tiene lotes." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {lotes.map((l) => (
            <div key={l.id} className="flex flex-col rounded-v25-md border border-v25-line bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-v25-ink-900">Lote {l.numero} · {l.nombre}</div>
                  <div className="truncate text-xs text-v25-ink-500" title={textoCriterio(l.criterio)}>{textoCriterio(l.criterio)}</div>
                </div>
                <span className="shrink-0 text-xs tabular-nums text-v25-ink-500">{l.total} personas</span>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-1 text-center">
                <Cifra v={l.realizados} t="Realizados" cls="text-v25-active-fg" />
                <Cifra v={l.con_cita} t="Con cita" cls="text-v25-progress-fg" />
                <Cifra v={l.sin_cita} t="Sin cita" cls="text-v25-waiting-fg" />
                <Cifra v={l.cancelados} t="Cancelados" cls="text-v25-ink-500" />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-v25-line pt-3">
                {l.notificado_en
                  ? <Badge tone="active">Avisado {fechaCorta(l.notificado_en)}</Badge>
                  : <Badge tone="waiting">Al activar</Badge>}
                {l.ultimo_recordatorio && (
                  <span className="text-xs text-v25-ink-500" data-ultimo-recordatorio>
                    Reavisado {fechaHoraCorta(l.ultimo_recordatorio.enviado_en)}
                    {l.ultimo_recordatorio.enviado_por ? ` · ${l.ultimo_recordatorio.enviado_por.nombre}` : ''}
                  </span>
                )}
                {puedeGestionar && (
                  <div className="flex items-center gap-1">
                    {enCurso && (
                      <Button variant="ghost" size="sm" onClick={() => setEditandoPersonas(l)}>
                        <Users className="w-3.5 h-3.5" /> Editar personas
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setRenombrando({ id: l.id, numero: l.numero, nombre: l.nombre })}>
                      <Pencil className="w-3.5 h-3.5" /> Renombrar
                    </Button>
                  </div>
                )}
              </div>
              {puedeGestionar && puedeReavisar(campana, l) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!!l.ultimo_recordatorio?.hoy}
                    onClick={() => setReavisando(l)}
                    data-reavisar={l.id}
                  >
                    <BellRing className="w-3.5 h-3.5" /> Volver a avisar
                  </Button>
                  {l.ultimo_recordatorio?.hoy && <span className="text-[11px] text-v25-ink-500">Ya se avisó hoy a este lote</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {nuevoAbierto && (
        <ModalNuevoLote
          open={nuevoAbierto}
          campana={campana}
          onClose={() => setNuevoAbierto(false)}
          onCreado={() => { setNuevoAbierto(false); cargar(); onCambio?.(); }}
        />
      )}

      {editandoPersonas && (
        <ModalPersonasLote
          open={!!editandoPersonas}
          campana={campana}
          lote={editandoPersonas}
          lotes={lotes || []}
          onClose={() => setEditandoPersonas(null)}
          onCambio={() => { cargar(); onCambio?.(); }}
        />
      )}

      {reavisando && (
        <ModalReavisarLote
          campanaId={campana.id}
          lote={reavisando}
          onClose={() => setReavisando(null)}
          onEnviado={() => { cargar(); onCambio?.(); }}
        />
      )}

      <ModalRenombrar
        lote={renombrando}
        campanaId={campana.id}
        onClose={() => setRenombrando(null)}
        onHecho={() => { setRenombrando(null); cargar(); onCambio?.(); }}
      />
    </section>
  );
}

function Cifra({ v, t, cls }) {
  return (
    <div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{v ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide text-v25-ink-500">{t}</div>
    </div>
  );
}

function ModalRenombrar({ lote, campanaId, onClose, onHecho }) {
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setNombre(lote?.nombre || ''); setError(null); }, [lote]);

  async function guardar() {
    setGuardando(true); setError(null);
    try {
      await renombrarLote(campanaId, lote.id, nombre);
      toast.success('Lote renombrado.');
      onHecho();
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo renombrar el lote.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open={!!lote}
      onClose={guardando ? () => {} : onClose}
      size="sm"
      title={lote ? `Renombrar el lote ${lote.numero}` : ''}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !nombre.trim()}>
            {guardando && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Guardar
          </Button>
        </>
      )}
    >
      <Input label="Nombre del lote" value={nombre} maxLength={120} onChange={(e) => setNombre(e.target.value)} error={error || undefined} />
    </Modal>
  );
}
